import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import {
  FileCheck,
  FileText,
  Home,
  Plus,
  AlertCircle,
  Info,
  X,
  CheckCircle,
  Circle,
  Sparkles,
  Upload,
  RefreshCw,
  Layers,
  Receipt,
  TrendingUp,
} from 'lucide-react'
import { SaleWizard } from './SaleWizard'
import { HubShell, StepTabs, ConfirmationToggle, type HubStep } from './HubShell'
import { DocumentSigningModal, type IdentificationData } from './DocumentSigningModal'
import { EsignDocumentsPanel, type EsignDocumentItem, type EsignParty } from './EsignDocumentsPanel'
import { documentExtraction } from '../../services/documentExtraction'
import { DocumentReadinessPanel } from '../../services/esign/DocumentReadinessPanel'
import { evaluateMultiple } from '../../services/esign/useDocumentReadiness'
import { Button } from '../ui/Button'
import { IdentificationFmvSummary } from '../identification/IdentificationFmvSummary'
import { DstAllocationSummary } from '../identification/DstAllocationSummary'
import { LikeKindPropertyForm } from '../identification/LikeKindPropertyForm'
import { IdentificationPropertyRow, type IdentifiedProperty } from '../identification/IdentificationPropertyRow'
import { IdSmartGuidanceBanner } from '../identification/IdSmartGuidanceBanner'
import { IdRuleComplianceBar } from '../identification/IdRuleComplianceBar'
import { IdentificationDstPicker, type DstPickerSelection } from '../identification/IdentificationDstPicker'
import toast from 'react-hot-toast'
import { useHubData } from '../../hooks/useHubData'
import { useHubAccess } from '../../hooks/useHubAccess'
import { useAuth } from '../../contexts/AuthContext'
import { useExchange } from '../../contexts/ExchangeContext'
import { activityLog } from '../../services/activityLog'
import { wizardApi } from '../../services/api/wizard'
import type { DocumentRenderData } from '../../services/esign/types'
import {
  evaluateRule,
  canAddProperty,
  validateIdentificationSubmit,
  type IdentificationRule,
} from '../../lib/identification/ruleEnforcement'
import { DEMO1_SALES_PRICE } from '@open1031/shared/demo/demo1'
import { LoadingSpinner } from '../ui/LoadingSpinner'

type ExchangeStep = 'investment-type' | 'settlement-statement' | 'identification-form' | 'documents'

type SettlementExtractionPhase = 'idle' | 'uploading' | 'extracting' | 'revealed'

interface BridgedIdentificationForm {
  exchangerNames: string
  relinquishedPropertyAddress: string
  idRule: string
  identifiedProperties: IdentifiedProperty[]
  investmentType?: string
}

interface WizardControls {
  goPrev: () => void
  goNext: () => void
  submit: (identificationOverride?: BridgedIdentificationForm) => void
  saveProgress: () => void
  canPrev: () => boolean
  canNext: () => boolean
  getPercent: () => number
  getLastSavedAt: () => string | null
  isStepComplete: (step: number) => boolean
  getPhase1StepCompletion: () => Record<string, boolean>
  setIdentificationForm: (
    updater: BridgedIdentificationForm | ((prev: BridgedIdentificationForm) => BridgedIdentificationForm)
  ) => void
}

interface BlockedSubmitPanelProps {
  idRule: string
  propertiesCount: number
  confirmed: boolean
  ruleValidation: { valid: boolean; blockReason?: string }
  allocationBalanced: boolean
  docsComplete: boolean
  documentsTabConfirmed: boolean
  totalAllocated: number
  fmvThreshold: number | null | undefined
}

const BlockedSubmitPanel: React.FC<BlockedSubmitPanelProps> = ({
  idRule,
  propertiesCount,
  confirmed,
  ruleValidation,
  allocationBalanced,
  docsComplete,
  documentsTabConfirmed,
  totalAllocated,
  fmvThreshold,
}) => {
  const allocationLabel = (() => {
    if (fmvThreshold == null || fmvThreshold <= 0 || propertiesCount === 0) return null
    const current = totalAllocated.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    })
    const required = fmvThreshold.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    })
    return `Allocate an amount to each identified property so they total the required value (currently ${current} of ${required})`
  })()
  const items: Array<{ label: string; satisfied: boolean }> = [
    {
      label: 'Pick an identification rule (3-property / 200% / 95%)',
      satisfied: !!idRule,
    },
    {
      label: 'Identify at least one replacement property',
      satisfied: propertiesCount > 0,
    },
    ...(allocationLabel && !allocationBalanced
      ? [{ label: allocationLabel, satisfied: false }]
      : []),
    {
      label: 'Confirm the identification details are accurate',
      satisfied: confirmed,
    },
    {
      label: 'E-sign the identification form in the Documents tab',
      satisfied: docsComplete,
    },
    {
      label: 'Confirm all documents are complete and accurate',
      satisfied: documentsTabConfirmed,
    },
    ...(ruleValidation.valid
      ? []
      : [{ label: ruleValidation.blockReason || 'IRS rule violation', satisfied: false }]),
  ]
  return (
    <div
      className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 px-4 py-3 space-y-1.5"
      role="status"
      aria-label="Before submitting checklist"
    >
      <p className="text-xs font-semibold text-blue-800 dark:text-blue-200">Before submitting:</p>
      {items.map((item) => (
        <div key={item.label} className="flex items-start gap-2">
          {item.satisfied ? (
            <CheckCircle
              className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5"
              aria-hidden="true"
            />
          ) : (
            <Circle
              className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5"
              aria-hidden="true"
            />
          )}
          <span
            className={
              item.satisfied
                ? 'text-xs text-blue-700 dark:text-blue-300 line-through'
                : 'text-xs text-blue-800 dark:text-blue-200'
            }
          >
            {item.label}
          </span>
        </div>
      ))}
    </div>
  )
}

const INVESTMENT_TYPE_OPTIONS: Array<{ value: string; label: string; description: string }> = [
  {
    value: 'like-kind-real-property',
    label: 'Like-kind real property',
    description: 'Direct replacement property — the most common 1031 path.',
  },
  {
    value: 'dst',
    label: 'Delaware Statutory Trust (DST)',
    description: 'Fractional interest in a professionally managed property portfolio.',
  },
  {
    value: 'tic',
    label: 'Tenants-in-Common (TIC)',
    description: 'Shared ownership interest in a single property with co-investors.',
  },
  {
    value: 'nnn',
    label: 'Triple Net Lease (NNN)',
    description: 'Tenant pays all operating expenses; predictable passive income.',
  },
  {
    value: 'oil-gas-mineral',
    label: 'Oil / Gas / Mineral royalty interest',
    description: 'Qualifying real property interest under IRC §1031.',
  },
  {
    value: 'leasehold',
    label: 'Leasehold (30+ years)',
    description: 'Long-term ground lease qualifying as like-kind real property.',
  },
  {
    value: 'other',
    label: 'Other',
    description: 'Another IRS-recognized like-kind real property interest.',
  },
]

const ID_RULE_OPTIONS: Array<{ value: string; label: string; description: string }> = [
  {
    value: '3-property',
    label: '3-Property Rule',
    description: 'Identify up to 3 properties of any value — the simplest path.',
  },
  {
    value: '200-percent',
    label: '200% Rule',
    description: 'Any number of properties; total identified FMV must not exceed 200% of relinquished sale price.',
  },
  {
    value: '95-percent',
    label: '95% Rule',
    description: 'Any number and value; you must close on at least 95% of total identified FMV.',
  },
]

const TOLERANCE = 1
const HUB_ID = 'identification'
const CONFIRMED_FMV = DEMO1_SALES_PRICE

function confirmationStorageKey(userId: string): string {
  return `${HUB_ID}_step_identify-properties_confirmed_${userId}`
}

function settlementConfirmationStorageKey(userId: string): string {
  return `${HUB_ID}_step_settlement_confirmed_${userId}`
}

function investmentTypeConfirmStorageKey(userId: string): string {
  return `${HUB_ID}_tab_investment-type_confirmed_${userId}`
}

function documentsTabConfirmStorageKey(userId: string): string {
  return `${HUB_ID}_tab_documents_confirmed_${userId}`
}

function readSalesPriceFromStorage(storageKey: string): number | null {
  try {
    const raw = localStorage.getItem(storageKey)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    const listPrice = parsed?.listPrice
    if (listPrice) {
      const numeric = parseFloat(String(listPrice).replace(/[^0-9.]/g, ''))
      if (numeric > 0) return numeric
    }
    return null
  } catch {
    return null
  }
}

function readExchangerDataFromStorage(storageKey: string) {
  try {
    const raw = localStorage.getItem(storageKey)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return parsed?.exchangerPhaseOne || null
  } catch {
    return null
  }
}

function readIdRuleFromExchangeStorage(exchangeStorageKey: string): string {
  try {
    const raw = localStorage.getItem(exchangeStorageKey)
    if (!raw) return ''
    const parsed = JSON.parse(raw)
    return parsed?.identificationForm?.idRule || ''
  } catch {
    return ''
  }
}

function readInvestmentTypesFromStorage(exchangeStorageKey: string): string[] {
  try {
    const raw = localStorage.getItem(exchangeStorageKey)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    const types = parsed?.identificationForm?.investmentTypes
    if (Array.isArray(types)) return types as string[]
    const legacy = parsed?.identificationForm?.investmentType
    if (typeof legacy === 'string' && legacy) return [legacy]
    return []
  } catch {
    return []
  }
}

function writeIdRuleToStorage(exchangeStorageKey: string, rule: string): void {
  try {
    const raw = localStorage.getItem(exchangeStorageKey)
    const parsed = raw ? JSON.parse(raw) : {}
    parsed.identificationForm = { ...(parsed.identificationForm || {}), idRule: rule }
    localStorage.setItem(exchangeStorageKey, JSON.stringify(parsed))
  } catch {}
}

function writeInvestmentTypesToStorage(exchangeStorageKey: string, types: string[]): void {
  try {
    const raw = localStorage.getItem(exchangeStorageKey)
    const parsed = raw ? JSON.parse(raw) : {}
    parsed.identificationForm = { ...(parsed.identificationForm || {}), investmentTypes: types }
    localStorage.setItem(exchangeStorageKey, JSON.stringify(parsed))
  } catch {}
}

interface SettlementRevealProps {
  confirmedAmount: number
}

const SettlementReveal: React.FC<SettlementRevealProps> = ({ confirmedAmount }) => {
  const formatted = confirmedAmount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  })
  return (
    <div
      className="rounded-xl border-2 border-emerald-300 dark:border-emerald-600 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/30 dark:to-teal-900/20 p-5"
      role="region"
      aria-label="Settlement statement confirmed"
      data-testid="settlement-reveal"
    >
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-800/40 flex items-center justify-center">
          <Sparkles className="h-5 w-5 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-100 mb-0.5">
            Neela confirmed your relinquished sale
          </p>
          <p className="text-xs text-emerald-700 dark:text-emerald-300 mb-3">
            Settlement statement processed — sale total verified against Sales Hub records.
          </p>
          <div className="rounded-lg bg-white/70 dark:bg-slate-800/50 border border-emerald-200 dark:border-emerald-700 px-4 py-3">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400 mb-0.5">
              Confirmed relinquished sale total
            </p>
            <p
              className="text-2xl font-bold text-emerald-800 dark:text-emerald-200 tabular-nums"
              data-testid="settlement-confirmed-amount"
            >
              {formatted}
            </p>
          </div>
          <div className="mt-3 flex items-center gap-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 px-3 py-2">
            <TrendingUp
              className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400 flex-shrink-0"
              aria-hidden="true"
            />
            <p className="text-xs text-blue-800 dark:text-blue-200">
              <span className="font-semibold">{formatted} to reinvest</span> — this becomes your FMV
              threshold for the 200% cap and 95% minimum rule enforcement.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

const EXTRACTING_STEPS = [
  'Reading document structure…',
  'Locating settlement totals…',
  'Cross-referencing Sales Hub…',
  'Confirming sale amount…',
]

interface SettlementExtractingProps {
  fileName: string
}

const SettlementExtracting: React.FC<SettlementExtractingProps> = ({ fileName }) => {
  const [stepIndex, setStepIndex] = useState(0)
  useEffect(() => {
    const timer = setInterval(() => {
      setStepIndex((prev) => Math.min(prev + 1, EXTRACTING_STEPS.length - 1))
    }, 500)
    return () => clearInterval(timer)
  }, [])
  return (
    <div
      className="rounded-xl border border-blue-200 dark:border-blue-700 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 p-5"
      role="status"
      aria-live="polite"
      aria-label="Neela is reading settlement statement"
      data-testid="settlement-extracting"
    >
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-800/40 flex items-center justify-center">
          <Sparkles
            className="h-5 w-5 text-blue-600 dark:text-blue-400 animate-pulse"
            aria-hidden="true"
          />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-0.5">
            Neela is reading your settlement statement…
          </p>
          <p className="text-xs text-blue-600 dark:text-blue-400 truncate mb-3">{fileName}</p>
          <div className="space-y-1.5">
            {EXTRACTING_STEPS.map((step, index) => (
              <div key={step} className="flex items-center gap-2">
                {index < stepIndex ? (
                  <CheckCircle
                    className="h-3 w-3 text-emerald-500 flex-shrink-0"
                    aria-hidden="true"
                  />
                ) : index === stepIndex ? (
                  <span
                    className="h-3 w-3 rounded-full border-2 border-blue-500 border-t-transparent animate-spin flex-shrink-0"
                    aria-hidden="true"
                  />
                ) : (
                  <Circle
                    className="h-3 w-3 text-blue-300 dark:text-blue-700 flex-shrink-0"
                    aria-hidden="true"
                  />
                )}
                <span
                  className={
                    index <= stepIndex
                      ? 'text-xs text-blue-800 dark:text-blue-200'
                      : 'text-xs text-blue-400 dark:text-blue-600'
                  }
                >
                  {step}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export const ExchangeHub: React.FC<{ onClose: () => void; onSectionComplete?: () => void }> = ({
  onClose,
  onSectionComplete,
}) => {
  const { user } = useAuth()
  const { currentExchange } = useExchange()
  const [activeStep, setActiveStep] = useState<ExchangeStep>('investment-type')
  const [isConfirmed, setIsConfirmedState] = useState(false)
  const [investmentTypeConfirmed, setInvestmentTypeConfirmed] = useState(false)
  const [documentsTabConfirmed, setDocumentsTabConfirmed] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(
    () => localStorage.getItem('identification_hub_submitted') === 'true'
  )
  const controlsRef = useRef<WizardControls | null>(null)
  const settlementFileInputRef = useRef<HTMLInputElement>(null)
  const { data: exchangeScopeData, getData, saveData, flush, hydrated } = useHubData('exchange')
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null)
  const access = useHubAccess('identification', user)

  const exchangeId = currentExchange?.id || user?.id || 'anon'
  const salesStorageKey = `sale_wizard_v1_${exchangeId}_phase1`
  const exchangeStorageKey = `sale_wizard_v1_${exchangeId}_exchange`

  const [salesPrice, setSalesPrice] = useState<number | null>(() =>
    readSalesPriceFromStorage(salesStorageKey)
  )

  const [properties, setProperties] = useState<IdentifiedProperty[]>(() => {
    try {
      const raw = localStorage.getItem(exchangeStorageKey)
      if (raw) {
        const parsed = JSON.parse(raw)
        const saved = parsed?.identificationProperties
        if (Array.isArray(saved) && saved.length > 0) return saved as IdentifiedProperty[]
      }
    } catch {}
    return []
  })

  const [idRule, setIdRuleState] = useState<string>(() =>
    readIdRuleFromExchangeStorage(exchangeStorageKey)
  )

  const [investmentTypes, setInvestmentTypesState] = useState<string[]>(() =>
    readInvestmentTypesFromStorage(exchangeStorageKey)
  )

  const [showLikeKindForm, setShowLikeKindForm] = useState(false)

  const [settlementUploaded, setSettlementUploaded] = useState<boolean>(() => {
    const saved = getData('settlementUploaded')
    return saved === true || localStorage.getItem('identification_settlement_uploaded') === 'true'
  })
  const [settlementFileName, setSettlementFileName] = useState<string>(() => {
    const saved = getData('settlementFileName')
    return (saved as string) || ''
  })
  const [settlementExtractionPhase, setSettlementExtractionPhase] =
    useState<SettlementExtractionPhase>(() => {
      const confirmed =
        getData('settlementAmountConfirmed') === true ||
        localStorage.getItem('identification_settlement_confirmed') === 'true'
      if (confirmed) return 'revealed'
      if (localStorage.getItem('identification_settlement_uploaded') === 'true') return 'idle'
      return 'idle'
    })
  const [settlementConfirmedAmount, setSettlementConfirmedAmount] = useState<number | null>(() => {
    const saved = getData('settlementConfirmedAmount')
    if (typeof saved === 'number' && saved > 0) return saved
    if (localStorage.getItem('identification_settlement_confirmed') === 'true') return CONFIRMED_FMV
    return null
  })
  const [settlementStepConfirmed, setSettlementStepConfirmed] = useState<boolean>(() => {
    return (
      localStorage.getItem(
        settlementConfirmationStorageKey(user?.id || '')
      ) === 'true' ||
      getData('settlementStepConfirmed') === true
    )
  })
  const settlementFileRef = useRef<File | null>(null)

  const fmvThreshold = useMemo(
    () => settlementConfirmedAmount ?? salesPrice,
    [settlementConfirmedAmount, salesPrice]
  )

  const settlementAmountFormatted =
    settlementConfirmedAmount != null
      ? settlementConfirmedAmount.toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 0,
        })
      : null

  const [identFormSigned, setIdentFormSigned] = useState<boolean>(() => {
    const saved = getData('identFormSigned')
    return saved === true || localStorage.getItem('identification_form_signed') === 'true'
  })

  const [identFormSignedBy, setIdentFormSignedBy] = useState<string>(
    () => localStorage.getItem('identification_form_signed_by') || ''
  )

  const [identFormSignedDate, setIdentFormSignedDate] = useState<string>(
    () => localStorage.getItem('identification_form_signed_date') || ''
  )

  const signedSignatureForView = useMemo((): DocumentRenderData['signature'] | undefined => {
    if (!identFormSigned) return undefined
    const rawSignatureValue =
      localStorage.getItem('identification_form_signature') ||
      identFormSignedBy ||
      (user?.firstName ? `${user.firstName} ${user.lastName ?? ''}`.trim() : 'Exchanger')
    const savedRaw = localStorage.getItem('user_saved_signature')
    let signatureType: 'type' | 'draw' = 'type'
    if (savedRaw) {
      try {
        const parsed = JSON.parse(savedRaw)
        if (parsed?.signatureMode === 'draw') signatureType = 'draw'
      } catch {}
    }
    return {
      type: signatureType,
      value: rawSignatureValue,
      captured: true,
      capturedAt: identFormSignedDate || new Date().toISOString(),
    }
  }, [identFormSigned, identFormSignedBy, identFormSignedDate, user])

  const [showSigningModal, setShowSigningModal] = useState(false)

  const [relinquishedSaleDate, setRelinquishedSaleDate] = useState<string>(() =>
    localStorage.getItem('property_sale_date') ?? ''
  )

  useEffect(() => {
    document.body.classList.add('modal-open')
    return () => {
      document.body.classList.remove('modal-open')
    }
  }, [])

  useEffect(() => {
    if (!user?.id) return
    const storedConfirm = localStorage.getItem(confirmationStorageKey(user.id))
    if (storedConfirm === 'true') setIsConfirmedState(true)
    const storedSettlement = localStorage.getItem(settlementConfirmationStorageKey(user.id))
    if (storedSettlement === 'true') setSettlementStepConfirmed(true)
    const storedInvestmentTypeConfirm = localStorage.getItem(investmentTypeConfirmStorageKey(user.id))
    if (storedInvestmentTypeConfirm === 'true') setInvestmentTypeConfirmed(true)
    const storedDocumentsTabConfirm = localStorage.getItem(documentsTabConfirmStorageKey(user.id))
    if (storedDocumentsTabConfirm === 'true') setDocumentsTabConfirmed(true)
  }, [user?.id])

  useEffect(() => {
    const saved = exchangeScopeData?.identificationProperties
    if (Array.isArray(saved) && saved.length > 0) {
      setProperties((prev) => {
        if (prev.length === 0) return saved as IdentifiedProperty[]
        return prev
      })
    }
  }, [exchangeScopeData])

  useEffect(() => {
    const savedRule = exchangeScopeData?.idRule
    if (typeof savedRule === 'string' && savedRule) {
      setIdRuleState((prev) => prev || savedRule)
    }
  }, [exchangeScopeData])

  useEffect(() => {
    const savedTypes = exchangeScopeData?.investmentTypes
    if (Array.isArray(savedTypes) && savedTypes.length > 0) {
      setInvestmentTypesState((prev) => (prev.length === 0 ? savedTypes as string[] : prev))
    }
  }, [exchangeScopeData])

  useEffect(() => {
    const handler = () => {
      setIsSubmitted(true)
      activityLog.logIdentificationSubmitted()
    }
    window.addEventListener('identificationHubCompleted', handler)
    return () => window.removeEventListener('identificationHubCompleted', handler)
  }, [])

  useEffect(() => {
    const check = () => {
      const submitted = localStorage.getItem('identification_hub_submitted') === 'true'
      if (submitted !== isSubmitted) setIsSubmitted(submitted)
    }
    check()
    const interval = setInterval(check, 2000)
    return () => clearInterval(interval)
  }, [isSubmitted])

  useEffect(() => {
    const refresh = () => {
      setSalesPrice(readSalesPriceFromStorage(salesStorageKey))
      setRelinquishedSaleDate(localStorage.getItem('property_sale_date') ?? '')
    }
    refresh()
    window.addEventListener('wizardDataUpdated', refresh)
    window.addEventListener('salesHubCompleted', refresh)
    return () => {
      window.removeEventListener('wizardDataUpdated', refresh)
      window.removeEventListener('salesHubCompleted', refresh)
    }
  }, [salesStorageKey])

  useEffect(() => {
    setLastSavedAt(exchangeScopeData?.lastSavedAt ?? null)
  }, [exchangeScopeData?.lastSavedAt])

  useEffect(() => {
    return () => {
      flush?.()
    }
  }, [flush])

  const navigateToStep = useCallback(
    (step: ExchangeStep) => {
      flush?.()
      setActiveStep(step)
    },
    [flush]
  )

  const setIdRule = useCallback(
    (rule: string) => {
      setIdRuleState(rule)
      writeIdRuleToStorage(exchangeStorageKey, rule)
      saveData({ idRule: rule })
      window.dispatchEvent(new CustomEvent('wizardDataUpdated'))
    },
    [exchangeStorageKey, saveData]
  )

  const setInvestmentTypes = useCallback(
    (types: string[]) => {
      setInvestmentTypesState(types)
      writeInvestmentTypesToStorage(exchangeStorageKey, types)
      saveData({ investmentTypes: types })
    },
    [exchangeStorageKey, saveData]
  )

  const handleConfirmChange = useCallback(
    (checked: boolean) => {
      setIsConfirmedState(checked)
      if (!user?.id) return
      if (checked) {
        localStorage.setItem(confirmationStorageKey(user.id), 'true')
      } else {
        localStorage.removeItem(confirmationStorageKey(user.id))
      }
    },
    [user?.id]
  )

  const handleInvestmentTypeConfirmChange = useCallback(
    (checked: boolean) => {
      setInvestmentTypeConfirmed(checked)
      if (!user?.id) return
      if (checked) {
        localStorage.setItem(investmentTypeConfirmStorageKey(user.id), 'true')
      } else {
        localStorage.removeItem(investmentTypeConfirmStorageKey(user.id))
      }
    },
    [user?.id]
  )

  const persistProperties = useCallback(
    (updated: IdentifiedProperty[]) => {
      saveData({ identificationProperties: updated })
    },
    [saveData]
  )

  const handleSettlementUpload = useCallback(
    (file: File) => {
      settlementFileRef.current = file
      setSettlementUploaded(true)
      setSettlementFileName(file.name)
      setSettlementExtractionPhase('uploading')
      localStorage.setItem('identification_settlement_uploaded', 'true')
      saveData({ settlementUploaded: true, settlementFileName: file.name })
      window.dispatchEvent(new CustomEvent('wizardDataUpdated'))
      setTimeout(() => {
        setSettlementExtractionPhase('extracting')
        documentExtraction
          .simulateExtraction(file, 'settlement-statement')
          .then((extracted) => {
            const amount =
              typeof extracted.relinquishedSalePrice === 'number' && extracted.relinquishedSalePrice > 0
                ? extracted.relinquishedSalePrice
                : CONFIRMED_FMV
            setSettlementConfirmedAmount(amount)
            setSettlementExtractionPhase('revealed')
            localStorage.setItem('identification_settlement_confirmed', 'true')
            saveData({
              settlementAmountConfirmed: true,
              settlementConfirmedAmount: amount,
            })
            activityLog.addActivity({
              type: 'document',
              title: 'Settlement Statement Processed',
              description: `Neela confirmed relinquished sale total: ${amount.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })}.`,
              status: 'completed',
            })
          })
          .catch(() => {
            setSettlementExtractionPhase('idle')
            toast.error('Extraction failed. Please try again.')
          })
      }, 400)
    },
    [saveData]
  )

  const handleSettlementReplace = useCallback(() => {
    setSettlementUploaded(false)
    setSettlementFileName('')
    setSettlementExtractionPhase('idle')
    setSettlementConfirmedAmount(null)
    setSettlementStepConfirmed(false)
    settlementFileRef.current = null
    localStorage.removeItem('identification_settlement_uploaded')
    localStorage.removeItem('identification_settlement_confirmed')
    if (user?.id) {
      localStorage.removeItem(settlementConfirmationStorageKey(user.id))
    }
    saveData({
      settlementUploaded: false,
      settlementFileName: '',
      settlementAmountConfirmed: false,
      settlementConfirmedAmount: null,
      settlementStepConfirmed: false,
    })
    window.dispatchEvent(new CustomEvent('wizardDataUpdated'))
    settlementFileInputRef.current?.click()
  }, [saveData, user?.id])

  const handleSettlementStepConfirmChange = useCallback(
    (checked: boolean) => {
      setSettlementStepConfirmed(checked)
      if (!user?.id) return
      if (checked) {
        localStorage.setItem(settlementConfirmationStorageKey(user.id), 'true')
        saveData({ settlementStepConfirmed: true })
      } else {
        localStorage.removeItem(settlementConfirmationStorageKey(user.id))
        saveData({ settlementStepConfirmed: false })
      }
    },
    [user?.id, saveData]
  )

  const handleDocumentsTabConfirmChange = useCallback(
    (checked: boolean) => {
      setDocumentsTabConfirmed(checked)
      if (!user?.id) return
      if (checked) {
        localStorage.setItem(documentsTabConfirmStorageKey(user.id), 'true')
        saveData({ documentsTabConfirmed: true })
      } else {
        localStorage.removeItem(documentsTabConfirmStorageKey(user.id))
        saveData({ documentsTabConfirmed: false })
      }
    },
    [user?.id, saveData]
  )

  const handleAddLikeKind = useCallback(
    (property: IdentifiedProperty) => {
      const typedRule = idRule as IdentificationRule
      if (typedRule) {
        const propertiesForRules = properties.map((p) => ({
          estimatedFMV: p.estimatedFMV || 0,
          allocationAmount: p.allocationAmount ?? 0,
          type: p.type,
        }))
        const addCheck = canAddProperty(typedRule, propertiesForRules, property.estimatedFMV, fmvThreshold)
        if (!addCheck.canAdd) {
          toast.error(addCheck.disabledReason || 'Cannot add this property under the current identification rule.')
          return
        }
      }
      setProperties((prev) => {
        const totalExisting = prev.reduce((sum, p) => sum + (p.allocationAmount || 0), 0)
        const remaining = fmvThreshold != null && fmvThreshold > 0
          ? Math.max(0, fmvThreshold - totalExisting)
          : (property.estimatedFMV || 0)
        const propertyWithAllocation: IdentifiedProperty = {
          ...property,
          allocationAmount: remaining,
        }
        const updated = [...prev, propertyWithAllocation]
        persistProperties(updated)
        return updated
      })
      setShowLikeKindForm(false)
    },
    [idRule, properties, fmvThreshold, persistProperties]
  )

  const handleAddDst = useCallback(
    (selection: DstPickerSelection) => {
      const duplicate = properties.some((p) => p.dstListingId === selection.dstListingId)
      if (duplicate) {
        toast.error('This DST is already added.')
        return
      }
      const newProperty: IdentifiedProperty = {
        address: selection.address,
        estimatedFMV: selection.estimatedFMV,
        type: 'dst',
        dstListingId: selection.dstListingId,
        dstTitle: selection.dstTitle,
        allocationAmount: selection.estimatedFMV,
        investmentAmount: selection.estimatedFMV,
      }
      const typedRule = idRule as IdentificationRule
      if (typedRule) {
        const propertiesForRules = properties.map((p) => ({
          estimatedFMV: p.estimatedFMV || 0,
          allocationAmount: p.allocationAmount ?? 0,
          type: p.type,
        }))
        const addCheck = canAddProperty(typedRule, propertiesForRules, newProperty.estimatedFMV, fmvThreshold)
        if (!addCheck.canAdd) {
          toast.error(addCheck.disabledReason || 'Cannot add this property under the current identification rule.')
          return
        }
      }
      setProperties((prev) => {
        const updated = [...prev, newProperty]
        persistProperties(updated)
        return updated
      })
    },
    [idRule, properties, fmvThreshold, persistProperties]
  )

  const handleUpdateProperty = useCallback(
    (index: number, updated: IdentifiedProperty) => {
      setProperties((prev) => {
        const next = prev.map((p, i) => (i === index ? updated : p))
        persistProperties(next)
        return next
      })
    },
    [persistProperties]
  )

  const handleRemoveProperty = useCallback(
    (index: number) => {
      setProperties((prev) => {
        const next = prev.filter((_, i) => i !== index)
        persistProperties(next)
        return next
      })
    },
    [persistProperties]
  )

  const handleDistributeEvenly = useCallback(() => {
    if (!fmvThreshold || properties.length === 0) return
    const perProperty = Math.floor(fmvThreshold / properties.length)
    const remainder = Math.round(fmvThreshold - perProperty * properties.length)
    setProperties((prev) => {
      const next = prev.map((p, i) => ({
        ...p,
        allocationAmount: i === 0 ? perProperty + remainder : perProperty,
      }))
      persistProperties(next)
      return next
    })
    toast.success('Distributed evenly across all properties')
  }, [fmvThreshold, properties.length, persistProperties])

  const handleIdentFormSigned = useCallback(
    (signature: string) => {
      const formComplete = !!idRule && properties.length > 0
      if (!formComplete || !isConfirmed) return
      const signedDate = new Date().toISOString()
      const signedBy =
        user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : 'User'
      setIdentFormSigned(true)
      setIdentFormSignedBy(signedBy)
      setIdentFormSignedDate(signedDate)
      setShowSigningModal(false)
      localStorage.setItem('identification_form_signed', 'true')
      localStorage.setItem('identification_form_signature', signature)
      localStorage.setItem('identification_form_signed_date', signedDate)
      window.dispatchEvent(new CustomEvent('wizardDataUpdated'))
      localStorage.setItem('identification_form_signed_by', signedBy)
      saveData({
        identFormSigned: true,
        identFormSignedDate: signedDate,
        identFormSignedBy: signedBy,
      })
      const currentExchangeId = currentExchange?.id || user?.id || 'anon'
      wizardApi
        .updateHubStatus(currentExchangeId, {
          documentSignatures: {
            identification_form: { signed: true, date: signedDate, by: signedBy },
          },
        })
        .catch((error: unknown) => {
          console.error('Failed to sync identification form signature to DB:', error)
        })
      activityLog.addActivity({
        type: 'document',
        title: 'Identification Form Signed',
        description: 'Identification of Replacement Property Form has been e-signed.',
        status: 'completed',
      })
      toast.success('Identification of Replacement Property Form signed successfully!')
    },
    [saveData, user, currentExchange, idRule, properties, isConfirmed]
  )

  const parsedProperties = useMemo(
    () =>
      properties.map((p) =>
        typeof p === 'string'
          ? { address: p as string, estimatedFMV: 0, type: 'dst' as const, allocationAmount: 0 }
          : {
              ...p,
              type: (p.type || 'dst') as IdentifiedProperty['type'],
              allocationAmount: p.allocationAmount ?? 0,
            }
      ),
    [properties]
  )

  const totalIdentifiedFMV = useMemo(
    () => parsedProperties.reduce((sum, p) => sum + (p.estimatedFMV || 0), 0),
    [parsedProperties]
  )

  const totalAllocated = useMemo(
    () => parsedProperties.reduce((sum, p) => sum + (p.allocationAmount || 0), 0),
    [parsedProperties]
  )

  const allocationBalanced = useMemo(() => {
    if (!fmvThreshold || parsedProperties.length === 0) return true
    return Math.abs(totalAllocated - fmvThreshold) <= TOLERANCE
  }, [fmvThreshold, parsedProperties.length, totalAllocated])

  const docsComplete = identFormSigned

  const investmentTypeComplete = investmentTypes.length > 0

  const settlementStepComplete =
    settlementExtractionPhase === 'revealed' && settlementConfirmedAmount !== null

  const formStepComplete =
    !!idRule && parsedProperties.length > 0 && allocationBalanced

  const allStepsComplete = formStepComplete && docsComplete

  const typedIdRule = idRule as IdentificationRule

  const ruleValidation = useMemo(() => {
    if (!typedIdRule) return { valid: true }
    return evaluateRule(typedIdRule, parsedProperties, fmvThreshold)
  }, [typedIdRule, parsedProperties, fmvThreshold])

  const submitValidation = useMemo(() => {
    if (!typedIdRule) return { valid: true, warnings: [] }
    return validateIdentificationSubmit(typedIdRule, parsedProperties, fmvThreshold)
  }, [typedIdRule, parsedProperties, fmvThreshold])

  const addPropertyDisabled = useMemo(() => {
    if (!typedIdRule) return false
    return !canAddProperty(typedIdRule, parsedProperties, 0, fmvThreshold).canAdd
  }, [typedIdRule, parsedProperties, fmvThreshold])

  const identificationWindowExpired = useMemo(() => {
    if (!relinquishedSaleDate) return false
    const datePart = relinquishedSaleDate.slice(0, 10)
    const [yearStr, monthStr, dayStr] = datePart.split('-')
    const saleYear = parseInt(yearStr, 10)
    const saleMonth = parseInt(monthStr, 10) - 1
    const saleDay = parseInt(dayStr, 10)
    if (isNaN(saleYear) || isNaN(saleMonth) || isNaN(saleDay)) return false
    const deadline = new Date(saleYear, saleMonth, saleDay + 45, 23, 59, 59, 999)
    return Date.now() > deadline.getTime()
  }, [relinquishedSaleDate])

  const canSignIdentForm = formStepComplete && isConfirmed && ruleValidation.valid

  const canSubmit =
    allStepsComplete &&
    isConfirmed &&
    documentsTabConfirmed &&
    !isSubmitted &&
    ruleValidation.valid &&
    !identificationWindowExpired

  const submitBlockedTitle = useMemo(() => {
    if (canSubmit || isSubmitted) return undefined
    const missing: string[] = []
    if (!idRule) missing.push('Pick an identification rule')
    if (parsedProperties.length === 0) missing.push('Identify at least one replacement property')
    if (!allocationBalanced && fmvThreshold != null && fmvThreshold > 0 && parsedProperties.length > 0) {
      const current = totalAllocated.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      })
      const required = fmvThreshold.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      })
      missing.push(`Allocate an amount to each identified property so they total the required value (currently ${current} of ${required})`)
    }
    if (!isConfirmed) missing.push('Confirm the identification details are accurate')
    if (!docsComplete) missing.push('E-sign the identification form in the Documents tab')
    if (!documentsTabConfirmed) missing.push('Confirm all documents are complete and accurate')
    if (!ruleValidation.valid && ruleValidation.blockReason) missing.push(ruleValidation.blockReason)
    if (identificationWindowExpired) missing.push('Identification period (45 days) has expired')
    return missing.join('; ')
  }, [canSubmit, isSubmitted, idRule, parsedProperties.length, allocationBalanced, fmvThreshold, totalAllocated, isConfirmed, docsComplete, documentsTabConfirmed, ruleValidation, identificationWindowExpired])

  const submitViolations = useMemo(() => {
    const violations: string[] = []
    if (!ruleValidation.valid && ruleValidation.blockReason) violations.push(ruleValidation.blockReason)
    if (fmvThreshold && fmvThreshold > 0 && parsedProperties.length > 0 && !allocationBalanced) {
      violations.push(
        `Allocation amounts must sum to the sales price (${fmvThreshold.toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 0,
        })}).`
      )
    }
    if (identificationWindowExpired) {
      violations.push('Identification period (45 days) has expired')
    }
    return violations
  }, [ruleValidation, fmvThreshold, parsedProperties.length, allocationBalanced, identificationWindowExpired])

  const liveCompletionPercent = useMemo(() => {
    if (isSubmitted) return 100
    const ruleSelected = !!idRule
    const hasProperty = parsedProperties.length > 0
    const confirmed = isConfirmed
    const settlementDone = settlementStepComplete
    const completed = [ruleSelected, hasProperty, confirmed, settlementDone].filter(Boolean).length
    return Math.round((completed / 4) * 100)
  }, [isSubmitted, idRule, parsedProperties.length, isConfirmed, settlementStepComplete])

  const exchangerData = useMemo(() => {
    const stored = readExchangerDataFromStorage(salesStorageKey)
    const fullName =
      stored?.names || (user?.firstName ? `${user.firstName} ${user.lastName ?? ''}`.trim() : '')
    const fullAddress =
      stored?.homeAddress || stored?.street
        ? [stored?.street, stored?.city, stored?.state, stored?.zipCode].filter(Boolean).join(', ')
        : ''
    return {
      names: fullName,
      homeAddress: fullAddress,
      taxIdNumber: stored?.taxIdNumber || '',
      street: stored?.street || '',
      city: stored?.city || '',
      state: stored?.state || '',
      zipCode: stored?.zipCode || '',
    }
  }, [salesStorageKey, user])

  const handleSubmit = useCallback(() => {
    try {
      if (!controlsRef.current?.submit) {
        toast.error('Wizard not ready. Please wait a moment and try again.')
        return
      }
      const relinquishedAddress =
        (getData('relinquished') as { addressOrLegalDescription?: string } | null)
          ?.addressOrLegalDescription || ''
      const identificationOverride: BridgedIdentificationForm = {
        exchangerNames: exchangerData.names,
        relinquishedPropertyAddress: relinquishedAddress,
        idRule,
        identifiedProperties: parsedProperties,
        investmentType: investmentTypes[0] || '',
      }
      controlsRef.current.submit(identificationOverride)
    } catch {
      toast.error('Failed to submit. Please try again.')
    }
  }, [exchangerData.names, getData, idRule, investmentTypes, parsedProperties])

  const identificationDeadline = useMemo(() => {
    if (relinquishedSaleDate) {
      const saleDate = new Date(relinquishedSaleDate)
      saleDate.setDate(saleDate.getDate() + 45)
      return saleDate.toLocaleDateString()
    }
    return '45 days from close of relinquished property sale'
  }, [relinquishedSaleDate])

  const exchangeAgreementDate = useMemo(() => {
    if (currentExchange?.createdAt) {
      return new Date(currentExchange.createdAt).toLocaleDateString()
    }
    return ''
  }, [currentExchange?.createdAt])

  const open1031FileNumber = useMemo(() => {
    if (!currentExchange?.id) return undefined
    const shortId = currentExchange.id.replace(/-/g, '').slice(0, 8).toUpperCase()
    return `OPEN1031-${shortId}`
  }, [currentExchange?.id])

  const identificationData = useMemo(
    (): IdentificationData => ({
      exchangerNames: exchangerData.names,
      identificationDeadline,
      identificationRule: (idRule as IdentificationData['identificationRule']) || '3-property',
      identifiedProperties: parsedProperties.map((p) => ({
        address: p.address,
        estimatedFMV: p.estimatedFMV,
        type: p.type,
        allocationAmount: p.allocationAmount,
        dstTitle: p.dstTitle,
        commonName: p.commonName,
        legalDescription: p.legalDescription,
        taxParcelNumber: p.taxParcelNumber,
        cityCountyState: p.cityCountyState,
        percentageInterest: p.percentageInterest,
        dollarValueAcquired: p.dollarValueAcquired,
      })),
      exchangeAgreementDate,
      relinquishedPropertySaleDate: relinquishedSaleDate || undefined,
      relinquishedPropertyFMV: fmvThreshold ?? undefined,
      open1031FileNumber,
      intermediaryReceiverName: 'Tom Bottenberg',
      intermediaryReceiverTitle: 'Chief Operations Officer',
    }),
    [
      exchangerData.names,
      identificationDeadline,
      idRule,
      parsedProperties,
      exchangeAgreementDate,
      relinquishedSaleDate,
      fmvThreshold,
      open1031FileNumber,
    ]
  )

  const documentRenderData = useMemo(
    (): DocumentRenderData => ({
      exchanger: exchangerData,
      identification: identificationData,
    }),
    [exchangerData, identificationData]
  )

  const { perDocument: docReadiness } = useMemo(
    () => evaluateMultiple(['identification-form'], documentRenderData),
    [documentRenderData]
  )

  const showDstPicker =
    investmentTypes.includes('dst') ||
    investmentTypes.includes('tic') ||
    investmentTypes.includes('nnn')

  const showLikeKindCta =
    investmentTypes.some((t) =>
      ['like-kind-real-property', 'oil-gas-mineral', 'leasehold', 'other', 'tic'].includes(t)
    ) || investmentTypes.includes('other')

  const showAddControls = investmentTypes.length > 0

  const steps: HubStep[] = [
    {
      key: 'investment-type',
      label: 'Investment Type',
      icon: Layers,
      completed: investmentTypeComplete,
    },
    {
      key: 'settlement-statement',
      label: 'Settlement Statement',
      icon: Receipt,
      completed: settlementStepComplete,
    },
    {
      key: 'identification-form',
      label: 'Identification Form',
      icon: FileCheck,
      completed: formStepComplete,
    },
    {
      key: 'documents',
      label: 'Documents',
      icon: FileText,
      completed: docsComplete && (documentsTabConfirmed || isSubmitted),
    },
  ]

  if (!access.canAccess) {
    return (
      <div className="modal-overlay">
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-md p-6 my-4">
          <div className="text-center">
            <FileCheck className="h-12 w-12 text-blue-600 dark:text-blue-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
              Identification Hub Locked
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
              {access.message || 'Please complete the Sales Hub first'}
            </p>
            <Button onClick={onClose}>
              <X className="h-4 w-4 mr-1.5" />
              Close
            </Button>
          </div>
        </div>
      </div>
    )
  }

  if (!hydrated && !exchangeScopeData) {
    return (
      <div className="modal-overlay">
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-md p-8 my-4 flex flex-col items-center gap-4">
          <LoadingSpinner size="lg" variant="icon" />
          <p className="text-sm text-slate-600 dark:text-slate-400">Loading hub data…</p>
        </div>
      </div>
    )
  }

  const relinquishedHubData = getData('relinquished') as { addressOrLegalDescription?: string } | null
  const initialData = {
    exchangerPhaseOne: getData('exchangerPhaseOne'),
    relinquished: relinquishedHubData,
    identificationForm: {
      ...(getData('identificationForm') as object | null),
      exchangerNames: exchangerData.names,
      relinquishedPropertyAddress: relinquishedHubData?.addressOrLegalDescription || '',
      idRule,
      identifiedProperties: parsedProperties,
      investmentType: investmentTypes[0] || '',
    },
  }

  const settlementViewDescription = settlementConfirmedAmount != null
    ? `Extracted and confirmed — sale total ${settlementConfirmedAmount.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      })}`
    : 'Processed in the Settlement Statement step'

  const settlementDocItems: EsignDocumentItem[] = settlementStepComplete
    ? [
        {
          key: 'settlement-statement-hud1',
          title: 'Settlement Statement (Relinquished)',
          description: settlementViewDescription,
          signed: true,
          actionType: 'view-only' as const,
          onView: () => toast.success('Settlement Statement available for download.'),
          disabled: false,
        },
      ]
    : []

  const settlementParties: EsignParty[] = [
    {
      label: 'Supporting Documents',
      documents: settlementDocItems,
    },
  ]

  return (
    <HubShell
      icon={FileCheck}
      title="Identification Hub"
      subtitle="Identify → allocate → confirm."
      completionPercent={liveCompletionPercent}
      isSubmitted={isSubmitted}
      color="blue"
      onClose={onClose}
      canSubmit={canSubmit}
      completedMessage="Identification Hub Completed"
      nextHubMessage="Purchase Hub is now unlocked"
      onSubmit={handleSubmit}
      submitLabel={
        isSubmitted
          ? 'Submitted'
          : !isConfirmed || !documentsTabConfirmed
            ? 'Confirm to Submit'
            : 'Submit Hub'
      }
      submitDisabledReason={submitBlockedTitle}
      lastSavedAt={lastSavedAt}
    >
      <StepTabs
        steps={steps}
        activeStep={activeStep}
        onStepChange={(key) => navigateToStep(key as ExchangeStep)}
        sectionTitle="During Exchange"
        color="blue"
        isSubmitted={isSubmitted}
      />

      {activeStep === 'investment-type' && (
        <div className="space-y-4">
          <div>
            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-1">
              What do you want to invest into?
            </h3>
            <p className="text-xs text-muted-foreground mb-3">
              Select all that apply. Your choices unlock the correct add-property options on the
              next tab.
            </p>
            <div className="space-y-2" role="group" aria-label="Investment type options">
              {INVESTMENT_TYPE_OPTIONS.map((option) => {
                const selected = investmentTypes.includes(option.value)
                return (
                  <button
                    key={option.value}
                    type="button"
                    disabled={isSubmitted}
                    onClick={() => {
                      const next = selected
                        ? investmentTypes.filter((t) => t !== option.value)
                        : [...investmentTypes, option.value]
                      setInvestmentTypes(next)
                    }}
                    aria-pressed={selected}
                    className={[
                      'w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg border-2 text-left transition-all',
                      selected
                        ? 'border-blue-600 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-border bg-card hover:border-blue-400 dark:hover:border-blue-500',
                      isSubmitted ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer',
                    ].join(' ')}
                  >
                    <div className="min-w-0 flex-1">
                      <p
                        className={[
                          'text-sm font-medium',
                          selected ? 'text-blue-700 dark:text-blue-300' : 'text-foreground',
                        ].join(' ')}
                      >
                        {option.label}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">{option.description}</p>
                    </div>
                    {selected && (
                      <CheckCircle
                        className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0"
                        aria-hidden="true"
                      />
                    )}
                  </button>
                )
              })}
            </div>
          </div>

          <ConfirmationToggle
            id="confirm-investment-type"
            label="I have selected my investment type(s) and am ready to proceed."
            checked={isSubmitted ? true : investmentTypeConfirmed}
            onChange={(checked) => {
              handleInvestmentTypeConfirmChange(checked)
              if (checked && !isSubmitted) navigateToStep('settlement-statement')
            }}
            disabled={isSubmitted || investmentTypes.length === 0}
            visible={true}
          />
        </div>
      )}

      {activeStep === 'settlement-statement' && (
        <div className="space-y-4">
          <div className="rounded-xl border border-blue-200 dark:border-blue-800 bg-gradient-to-br from-blue-50/80 to-indigo-50/60 dark:from-blue-900/20 dark:to-indigo-900/10 px-4 py-3">
            <div className="flex items-start gap-3">
              <Receipt
                className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0"
                aria-hidden="true"
              />
              <div>
                <p className="text-sm font-semibold text-blue-900 dark:text-blue-100">
                  Upload your Settlement Statement
                </p>
                <p className="text-xs text-blue-700 dark:text-blue-300 mt-0.5 leading-relaxed">
                  Neela will read the document, confirm your relinquished sale total, and set the
                  FMV threshold your identification rules enforce against.
                </p>
              </div>
            </div>
          </div>

          {settlementExtractionPhase === 'idle' && !settlementUploaded && (
            <button
              type="button"
              onClick={() => settlementFileInputRef.current?.click()}
              disabled={isSubmitted}
              aria-label="Upload settlement statement"
              data-testid="settlement-upload-button"
              className={[
                'w-full flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed px-6 py-10 transition-all',
                isSubmitted
                  ? 'border-slate-200 dark:border-slate-700 opacity-60 cursor-not-allowed'
                  : 'border-blue-300 dark:border-blue-700 hover:border-blue-500 dark:hover:border-blue-500 hover:bg-blue-50/50 dark:hover:bg-blue-900/10 cursor-pointer',
              ].join(' ')}
            >
              <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
                <Upload className="h-6 w-6 text-blue-600 dark:text-blue-400" aria-hidden="true" />
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-blue-800 dark:text-blue-200">
                  Drop your Settlement Statement here
                </p>
                <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
                  PDF, JPG, or PNG · Neela reads it instantly
                </p>
              </div>
            </button>
          )}

          {settlementExtractionPhase === 'uploading' && (
            <div
              className="rounded-xl border border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 p-4 flex items-center gap-3"
              role="status"
              aria-live="polite"
            >
              <span
                className="h-5 w-5 rounded-full border-2 border-blue-500 border-t-transparent animate-spin flex-shrink-0"
                aria-hidden="true"
              />
              <p className="text-sm text-blue-800 dark:text-blue-200">
                Uploading {settlementFileName}…
              </p>
            </div>
          )}

          {settlementExtractionPhase === 'extracting' && (
            <SettlementExtracting fileName={settlementFileName} />
          )}

          {settlementExtractionPhase === 'revealed' && settlementConfirmedAmount !== null && (
            <>
              <SettlementReveal confirmedAmount={settlementConfirmedAmount} />
              {!isSubmitted && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleSettlementReplace}
                    aria-label="Replace settlement statement"
                    className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
                  >
                    <RefreshCw className="h-3 w-3" aria-hidden="true" />
                    Replace document
                  </button>
                </div>
              )}
            </>
          )}

          <input
            ref={settlementFileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            className="sr-only"
            aria-label="Upload settlement statement file"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) {
                handleSettlementUpload(file)
                e.target.value = ''
              }
            }}
          />

          <ConfirmationToggle
            id="confirm-settlement-statement"
            label={
              settlementAmountFormatted
                ? `I confirm the relinquished sale total of ${settlementAmountFormatted} and am ready to proceed.`
                : 'I confirm the relinquished sale total and am ready to proceed.'
            }
            checked={isSubmitted ? true : settlementStepConfirmed}
            onChange={(checked) => {
              handleSettlementStepConfirmChange(checked)
              if (checked && !isSubmitted && settlementStepComplete) {
                navigateToStep('identification-form')
              }
            }}
            disabled={isSubmitted || !settlementStepComplete}
            visible={true}
          />
        </div>
      )}

      {activeStep === 'identification-form' && (
        <div className="space-y-4">
          {settlementConfirmedAmount !== null && (
            <div className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50/60 dark:bg-emerald-900/10 px-3 py-2.5 flex items-center gap-2">
              <CheckCircle
                className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400 flex-shrink-0"
                aria-hidden="true"
              />
              <p className="text-xs text-emerald-800 dark:text-emerald-200">
                <span className="font-semibold">FMV threshold: {settlementAmountFormatted}</span>
                {' '}— confirmed from settlement statement. All rule calculations use this amount.
              </p>
            </div>
          )}

          <SaleWizard
            embedded
            hideFooter
            exposeControls={(api) => (controlsRef.current = api as WizardControls)}
            mode="exchange"
            onClose={onClose}
            onComplete={() => {
              onSectionComplete?.()
            }}
            initialData={initialData}
          />

          <div className="space-y-1.5">
            <p className="text-xs font-semibold text-blue-700 dark:text-blue-300">
              ID Rule <span className="text-red-500">*</span>
            </p>
            <div
              className="space-y-1.5"
              role="group"
              aria-label="Identification rule options"
            >
              {ID_RULE_OPTIONS.map((option) => {
                const selected = idRule === option.value
                return (
                  <button
                    key={option.value}
                    type="button"
                    disabled={isSubmitted}
                    onClick={() => {
                      const typedNewRule = option.value as IdentificationRule
                      if (typedNewRule && parsedProperties.length > 0) {
                        const ruleResult = evaluateRule(typedNewRule, parsedProperties, fmvThreshold)
                        if (!ruleResult.valid) {
                          toast.error(
                            ruleResult.blockReason ||
                              'Cannot switch to this rule with the current identified properties.'
                          )
                          return
                        }
                      }
                      setIdRule(option.value)
                    }}
                    aria-pressed={selected}
                    className={[
                      'w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg border-2 text-left transition-all',
                      selected
                        ? 'border-blue-600 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-border bg-card hover:border-blue-400 dark:hover:border-blue-500',
                      isSubmitted ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer',
                    ].join(' ')}
                  >
                    <div className="min-w-0 flex-1">
                      <p
                        className={[
                          'text-xs font-semibold',
                          selected ? 'text-blue-700 dark:text-blue-300' : 'text-foreground',
                        ].join(' ')}
                      >
                        {option.label}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                        {option.description}
                      </p>
                    </div>
                    {selected && (
                      <CheckCircle
                        className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0"
                        aria-hidden="true"
                      />
                    )}
                  </button>
                )
              })}
            </div>
          </div>

          <IdSmartGuidanceBanner
            properties={parsedProperties}
            idRule={typedIdRule || ''}
            relinquishedFMV={fmvThreshold}
          />

          {showAddControls ? (
            <div className="space-y-2">
              {(showLikeKindCta || investmentTypes.length > 0) &&
                !investmentTypes.every((t) => t === 'dst') && (
                  showLikeKindForm ? (
                    <LikeKindPropertyForm
                      onAdd={handleAddLikeKind}
                      onCancel={() => setShowLikeKindForm(false)}
                      rule={typedIdRule}
                      existingProperties={parsedProperties.map((p) => ({
                        estimatedFMV: p.estimatedFMV || 0,
                      }))}
                      relinquishedFMV={fmvThreshold}
                    />
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowLikeKindForm(true)}
                      disabled={isSubmitted || addPropertyDisabled}
                      className="w-full border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                      aria-label="Add a like-kind property"
                    >
                      <Plus className="h-4 w-4 mr-1.5" aria-hidden="true" />
                      <Home className="h-4 w-4 mr-1.5" aria-hidden="true" />
                      Add a like-kind property
                    </Button>
                  )
                )}

              {showDstPicker && (
                <IdentificationDstPicker
                  onSelect={handleAddDst}
                  disabled={isSubmitted || addPropertyDisabled}
                  rule={typedIdRule || undefined}
                  existingProperties={parsedProperties.map((p) => ({
                    estimatedFMV: p.estimatedFMV || 0,
                  }))}
                  relinquishedFMV={fmvThreshold}
                  alreadyAddedIds={parsedProperties
                    .map((p) => p.dstListingId)
                    .filter((id): id is string => id != null)}
                />
              )}
            </div>
          ) : (
            <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/10 px-4 py-3">
              <p className="text-xs text-blue-800 dark:text-blue-200">
                Select your investment type(s) on the Investment Type tab to unlock the add-property
                options.
              </p>
            </div>
          )}

          <div className="space-y-2">
            {parsedProperties.map((property, index) => (
              <IdentificationPropertyRow
                key={index}
                property={property}
                index={index}
                showAllocationInput={fmvThreshold != null && fmvThreshold > 0}
                salesPrice={fmvThreshold}
                disabled={isSubmitted}
                onUpdate={(updated) => handleUpdateProperty(index, updated)}
                onRemove={() => handleRemoveProperty(index)}
              />
            ))}

            {parsedProperties.length === 0 && (
              <div
                className="rounded-lg border border-dashed border-blue-300 dark:border-blue-700 bg-blue-50/30 dark:bg-blue-900/10 px-4 py-4 text-center"
                aria-label="No properties identified yet"
              >
                <p className="text-xs text-blue-700 dark:text-blue-400">
                  No properties added yet.
                </p>
              </div>
            )}
          </div>

          {fmvThreshold && fmvThreshold > 0 && parsedProperties.length > 0 && (
            <DstAllocationSummary
              properties={parsedProperties}
              salesPrice={fmvThreshold}
              idRule={idRule}
              disabled={isSubmitted}
              onDistributeEvenly={handleDistributeEvenly}
            />
          )}

          {(idRule === '200-percent' || idRule === '95-percent') && totalIdentifiedFMV > 0 && (
            <IdentificationFmvSummary
              totalIdentifiedFMV={totalIdentifiedFMV}
              relinquishedFMV={fmvThreshold}
            />
          )}

          <IdRuleComplianceBar
            properties={parsedProperties}
            idRule={typedIdRule || ''}
            relinquishedFMV={fmvThreshold}
            disabled={isSubmitted}
            onDistributeEvenly={handleDistributeEvenly}
            confirmedFMV={settlementConfirmedAmount}
            totalAllocated={totalAllocated}
          />

          {submitViolations.length > 0 && (
            <div
              className="flex items-start gap-2 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-4 py-3"
              role="alert"
              aria-live="polite"
            >
              <AlertCircle
                className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5"
                aria-hidden="true"
              />
              <div className="space-y-1">
                <p className="text-xs font-semibold text-amber-800 dark:text-amber-200">
                  Cannot submit:
                </p>
                {submitViolations.map((violation, index) => (
                  <p key={index} className="text-xs text-amber-800 dark:text-amber-200">
                    {violation}
                  </p>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeStep === 'documents' && (
        <div className="space-y-4">
          {submitValidation.warnings.length > 0 &&
            submitValidation.warnings.map((warning, index) => (
              <div
                key={index}
                className="flex items-start gap-2 rounded-lg border border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20 px-4 py-3"
                role="status"
                aria-live="polite"
              >
                <Info
                  className="h-4 w-4 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5"
                  aria-hidden="true"
                />
                <p className="text-xs text-yellow-800 dark:text-yellow-200">{warning}</p>
              </div>
            ))}
          <DocumentReadinessPanel readiness={docReadiness} />

          <div
            className={[
              'rounded-xl border-2 p-4 space-y-3',
              identFormSigned
                ? 'border-emerald-300 dark:border-emerald-600 bg-emerald-50/60 dark:bg-emerald-900/10'
                : 'border-blue-300 dark:border-blue-700 bg-blue-50/60 dark:bg-blue-900/10',
            ].join(' ')}
            role="region"
            aria-label="E-sign required: Identification of Replacement Property Form"
          >
            <div className="flex items-start gap-3">
              {identFormSigned ? (
                <CheckCircle
                  className="h-5 w-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5"
                  aria-hidden="true"
                />
              ) : (
                <FileText
                  className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5"
                  aria-hidden="true"
                />
              )}
              <div className="flex-1 min-w-0">
                <p
                  className={[
                    'text-sm font-semibold',
                    identFormSigned
                      ? 'text-emerald-900 dark:text-emerald-100'
                      : 'text-blue-900 dark:text-blue-100',
                  ].join(' ')}
                >
                  Identification of Replacement Property Form
                </p>
                <p
                  className={[
                    'text-xs mt-0.5',
                    identFormSigned
                      ? 'text-emerald-700 dark:text-emerald-300'
                      : 'text-blue-700 dark:text-blue-300',
                  ].join(' ')}
                >
                  {identFormSigned
                    ? `Signed${identFormSignedBy ? ` by ${identFormSignedBy}` : ''}${identFormSignedDate ? ` on ${new Date(identFormSignedDate).toLocaleDateString()}` : ''}`
                    : 'E-sign to certify your replacement property identifications'}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {identFormSigned ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowSigningModal(true)}
                    className="text-xs border-emerald-400 dark:border-emerald-600 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/30"
                    aria-label="View signed Identification of Replacement Property Form"
                  >
                    <TrendingUp className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
                    View Signed
                  </Button>
                ) : (
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => setShowSigningModal(true)}
                    disabled={isSubmitted || !canSignIdentForm}
                    className="text-xs bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white"
                    aria-label="E-Sign Identification of Replacement Property Form"
                  >
                    <FileText className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
                    E-Sign
                  </Button>
                )}
              </div>
            </div>
            {!identFormSigned && !canSignIdentForm && !isSubmitted && (
              <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                Complete and confirm the identification before signing
              </p>
            )}
          </div>

          {settlementStepComplete && (
            <EsignDocumentsPanel
              parties={settlementParties}
              readOnly={isSubmitted}
              color="blue"
              showSectionHeader={false}
            />
          )}

          <ConfirmationToggle
            id="confirm-identification-documents-tab"
            label="I confirm all documents are complete and accurate, and I'm ready to submit."
            checked={isSubmitted ? true : documentsTabConfirmed}
            onChange={handleDocumentsTabConfirmChange}
            disabled={isSubmitted || !docsComplete}
            visible={docsComplete || isSubmitted}
          />
        </div>
      )}

      {activeStep === 'identification-form' && !canSubmit && !isSubmitted && (
        <div className="mt-6">
          <BlockedSubmitPanel
            idRule={idRule}
            propertiesCount={parsedProperties.length}
            confirmed={isConfirmed}
            ruleValidation={ruleValidation}
            allocationBalanced={allocationBalanced}
            docsComplete={docsComplete}
            documentsTabConfirmed={documentsTabConfirmed}
            totalAllocated={totalAllocated}
            fmvThreshold={fmvThreshold}
          />
        </div>
      )}

      {activeStep === 'identification-form' && (
        <ConfirmationToggle
          id="confirm-identification"
          label="I confirm the identification details are complete and accurate."
          checked={isSubmitted ? true : isConfirmed}
          onChange={(checked) => {
            handleConfirmChange(checked)
            if (checked && !isSubmitted && idRule && parsedProperties.length > 0 && ruleValidation.valid) {
              navigateToStep('documents')
            }
          }}
          disabled={isSubmitted || !idRule || parsedProperties.length === 0 || !ruleValidation.valid}
          visible={true}
        />
      )}

      {showSigningModal && (
        <DocumentSigningModal
          documentName="Identification of Replacement Property Form"
          documentType="identification-form"
          exchangerData={exchangerData}
          identificationData={identificationData}
          signedSignature={(identFormSigned || isSubmitted) ? signedSignatureForView : undefined}
          signerEmail={user?.email || ''}
          exchangeId={currentExchange?.id || user?.id || ''}
          onSign={handleIdentFormSigned}
          onClose={() => setShowSigningModal(false)}
          viewOnly={identFormSigned || isSubmitted}
        />
      )}
    </HubShell>
  )
}
